import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function CustomersSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="space-y-2">
          <div className="h-8 w-32 bg-muted rounded animate-pulse"></div>
          <div className="h-4 w-56 bg-muted rounded animate-pulse"></div>
        </div>
        <div className="h-10 w-36 bg-muted rounded animate-pulse"></div>
      </div>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="h-10 flex-1 bg-muted rounded animate-pulse"></div>
            <div className="h-10 w-40 bg-muted rounded animate-pulse"></div>
            <div className="flex gap-2">
              <div className="h-10 w-10 bg-muted rounded animate-pulse"></div>
              <div className="h-10 w-10 bg-muted rounded animate-pulse"></div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="p-4 flex items-center gap-4 animate-pulse">
                <div className="h-10 w-10 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/3"></div>
                  <div className="h-3 bg-muted rounded w-1/4"></div>
                </div>
                <div className="h-6 w-20 bg-muted rounded"></div>
                <div className="h-8 w-16 bg-muted rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus,
  Search,
  Eye,
  Edit,
  Building,
  Mail,
  Phone,
  Trash2,
  Upload,
  Download,
  Grid3X3,
  List,
  ArrowUpDown,
  RefreshCw,
} from "lucide-react";
import { Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/queryClient";
import { insertCustomerSchema, CUSTOMER_INDUSTRIES } from "@shared/schema";
import { useAuth } from "@/hooks/useAuth";
import { shouldShowComponent } from "@/lib/rbac";

export default function Customers() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<any>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importResults, setImportResults] = useState<any>(null);

  // Get current organization for organization ID
  const { data: organization } = useQuery<any>({
    queryKey: ["/api/organizations/current"],
    queryFn: () => apiRequest("GET", "/api/organizations/current"),
  });

  const { data: customers, isLoading } = useQuery<any[]>({
    queryKey: ["/api/customers"],
  });

  const { data: customerPsaMappings = [] } = useQuery<any[]>({
    queryKey: ["/api/customers/psa-mappings"],
  });

  // Fetch Asio RMM mappings for sync badge display
  const { data: asioRmmMappings = [] } = useQuery<any[]>({
    queryKey: ["/api/integrations/connectwise-asio/mappings"],
    queryFn: async () => {
      const response = await fetch(`/api/integrations/connectwise-asio/mappings`);
      if (!response.ok) return [];
      return response.json();
    },
  });

  // Fetch integrations to check if PSA is enabled (use status endpoint accessible to all users)
  const { data: integrations = [] } = useQuery<any[]>({
    queryKey: ["/api/integrations/status"],
  });

  // Get active PSA integration (ConnectWise or similar)
  const activePsaIntegration = integrations.find(
    (i: any) => i.provider === "connectwise" && i.status === "active"
  );

  // Sync customers from PSA mutation
  const syncCustomersMutation = useMutation({
    mutationFn: async (integrationId: number) => {
      return apiRequest(
        "POST",
        `/api/integrations/${integrationId}/sync-customers`,
      );
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/customers"] });
      toast({
        title: "Customer Sync Complete",
        description: data.message || "Customers synced successfully from PSA",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to sync customers from PSA",
        variant: "destructive",
      });
    },
  });

  const createCustomerForm = useForm({
    resolver: zodResolver(
      insertCustomerSchema
        .omit({ organizationId: true })
        // firstReviewDate is not a customers column — it's an onboarding-only
        // field the backend reads to schedule the customer's first review.
        .extend({ firstReviewDate: z.string().optional() }),
    ),
    defaultValues: {
      name: "",
      type: "small-business",
      industry: undefined,
      addressStreet: "",
      addressCity: "",
      addressState: "",
      addressCountry: "",
      addressZipCode: "",
      reviewFrequency: "quarterly",
      firstReviewDate: new Date().toISOString().slice(0, 10),
    },
  });

  const createCustomerMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiRequest("POST", "/api/customers", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/customers"] });
      setShowCreateForm(false);
      createCustomerForm.reset();
      toast({
        title: "Success",
        description: "Customer created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create customer",
        variant: "destructive",
      });
    },
  });

  const importCustomersMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("csvFile", file);

      // Import the secure API request function
      const { secureApiRequest } = await import("@/lib/csrfClient");

      const response = await secureApiRequest("/api/customers/import", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Import failed");
      }

      return response.json();
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: ["/api/customers"] });
      setImportResults(results);
      setCsvFile(null);
      toast({
        title: "Import Complete",
        description: `${results.successful} customers imported successfully${results.failed > 0 ? `, ${results.failed} failed` : ""}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Import Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const downloadTemplate = async () => {
    try {
      // First, try the client-side approach
      const headers = [
        "name",
        "type",
        "addressStreet",
        "addressCity",
        "addressState",
        "addressCountry",
        "addressZipCode",
      ];

      const sampleData = [
        "Acme Corporation",
        "enterprise",
        "123 Business St",
        "Anytown",
        "CA",
        "Los Angeles",
        "90210",
      ];

      const sampleData2 = [
        "TechFlow Solutions",
        "mid-market",
        "456 Tech Avenue",
        "Silicon Valley",
        "CA",
        "Santa Clara",
        "95050",
      ];

      const csvContent = [
        headers.join(","),
        sampleData.join(","),
        sampleData2.join(","),
      ].join("\n");

      // Try client-side download first
      try {
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "customer-import-template.csv";
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);

        toast({
          title: "Template Downloaded",
          description: "CSV template has been downloaded to your device",
        });
      } catch (clientError) {
        // Fallback to server-side download
        const link = document.createElement("a");
        link.href = "/api/customers/template";
        link.download = "customer-import-template.csv";
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
          title: "Template Downloaded",
          description: "CSV template has been downloaded via server",
        });
      }
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Failed to download template. Please contact support.",
        variant: "destructive",
      });
    }
  };

  const handleImport = () => {
    if (csvFile) {
      importCustomersMutation.mutate(csvFile);
    }
  };

  const updateCustomerMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      await apiRequest("PATCH", `/api/customers/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/customers"] });
      setEditingCustomer(null);
      createCustomerForm.reset();
      toast({
        title: "Success",
        description: "Customer updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update customer",
        variant: "destructive",
      });
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/customers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/customers"] });
      toast({
        title: "Success",
        description: "Customer deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete customer",
        variant: "destructive",
      });
    },
  });

  const getTypeBadge = (type: string) => {
    const typeMap = {
      enterprise: { label: "Enterprise", variant: "default" as const },
      "mid-market": { label: "Mid-market", variant: "secondary" as const },
      "small-business": {
        label: "Small Business",
        variant: "outline" as const,
      },
    };

    const typeInfo = typeMap[type as keyof typeof typeMap] || {
      label: type,
      variant: "default" as const,
    };
    return <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>;
  };

  const getInitials = (name: string) => {
    return (
      name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase() || "?"
    );
  };

  const isPsaSynced = (customerId: number) => {
    return customerPsaMappings.some(
      (mapping: any) => mapping.customerId === customerId,
    ) || asioRmmMappings.some(
      (mapping: any) => mapping.customerId === customerId,
    );
  };

  const getPsaProvider = (customerId: number) => {
    const mapping = customerPsaMappings.find(
      (mapping: any) => mapping.customerId === customerId,
    );
    if (mapping) return mapping?.integration?.provider || null;
    const asioMapping = asioRmmMappings.find(
      (m: any) => m.customerId === customerId,
    );
    if (asioMapping) return "connectwise_asio";
    return null;
  };

  const getProviderDisplayName = (provider: string | null) => {
    if (!provider) return "PSA";
    switch (provider) {
      case "connectwise_asio": return "ConnectWise Platform";
      case "connectwise": return "ConnectWise";
      case "halopsa": return "HaloPSA";
      default: return provider.toUpperCase();
    }
  };

  const filteredCustomers =
    customers
      ?.filter((customer: any) => {
        const matchesSearch =
          customer.name.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesType =
          typeFilter === "all" || customer.type === typeFilter;

        return matchesSearch && matchesType;
      })
      .sort((a: any, b: any) => {
        let aValue, bValue;

        switch (sortBy) {
          case "name":
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case "type":
            aValue = a.type;
            bValue = b.type;
            break;
          case "createdAt":
          default:
            aValue = new Date(a.createdAt);
            bValue = new Date(b.createdAt);
            break;
        }

        if (sortOrder === "asc") {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      }) || [];

  const onSubmit = (data: any) => {
    if (editingCustomer) {
      updateCustomerMutation.mutate({ id: editingCustomer.id, data });
    } else {
      // Add organization ID to customer data
      const customerData = {
        ...data,
        organizationId: organization?.id,
      };
      createCustomerMutation.mutate(customerData);
    }
  };

  const startEdit = (customer: any) => {
    setEditingCustomer(customer);
    createCustomerForm.reset({
      name: customer.name,
      type: customer.type,
      industry: customer.industry || undefined,
      addressStreet: customer.addressStreet || "",
      addressCity: customer.addressCity || "",
      addressState: customer.addressState || "",
      addressCountry: customer.addressCountry || "",
      addressZipCode: customer.addressZipCode || "",
      reviewFrequency: customer.reviewFrequency || "quarterly",
    });
    setShowCreateForm(true);
  };

  const handleDelete = (customer: any) => {
    if (
      confirm(
        `Are you sure you want to delete "${customer.name}"? This action cannot be undone.`,
      )
    ) {
      deleteCustomerMutation.mutate(customer.id);
    }
  };

  const resetForm = () => {
    setShowCreateForm(false);
    setEditingCustomer(null);
    createCustomerForm.reset();
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-8"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
            <Building className="h-6 w-6 md:h-8 md:w-8" />
            Customers
          </h1>
          <p className="text-muted-foreground">
            Manage your client base and customer information
          </p>
        </div>
        {shouldShowComponent(user, "createCustomer") && (
          <div className="flex gap-2">
            {activePsaIntegration && (
              <Button
                variant="outline"
                onClick={() => syncCustomersMutation.mutate(activePsaIntegration.id)}
                disabled={syncCustomersMutation.isPending}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${syncCustomersMutation.isPending ? "animate-spin" : ""}`} />
                {syncCustomersMutation.isPending ? "Syncing..." : "Sync from PSA"}
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowImportDialog(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Bulk Import
            </Button>
            <Button
              className="bg-primary hover:bg-blue-700"
              data-onboarding="add-customer"
              onClick={() => {
                setEditingCustomer(null);
                createCustomerForm.reset({
                  name: "",
                  type: "small-business",
                  industry: undefined,
                  addressStreet: "",
                  addressCity: "",
                  addressState: "",
                  addressCountry: "",
                  addressZipCode: "",
                  reviewFrequency: "quarterly",
                  firstReviewDate: new Date().toISOString().slice(0, 10),
                });
                setShowCreateForm(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Customer
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Customer Dialog */}
      <Dialog
        open={showCreateForm}
        onOpenChange={(open) => {
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? "Edit Customer" : "Add New Customer"}
            </DialogTitle>
          </DialogHeader>
          <Form {...createCustomerForm}>
            <form
              onSubmit={createCustomerForm.handleSubmit(onSubmit)}
              className="space-y-3"
            >
              <FormField
                control={createCustomerForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter company name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={createCustomerForm.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select business type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="small-business">
                          Small Business
                        </SelectItem>
                        <SelectItem value="mid-market">Mid-market</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={createCustomerForm.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="financial_services">
                          Financial Services
                        </SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="legal">Legal</SelectItem>
                        <SelectItem value="manufacturing">
                          Manufacturing
                        </SelectItem>
                        <SelectItem value="professional_services">
                          Professional Services
                        </SelectItem>
                        <SelectItem value="technology">Technology</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                        <SelectItem value="government">Government</SelectItem>
                        <SelectItem value="nonprofit">Nonprofit</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={createCustomerForm.control}
                name="reviewFrequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Review Cadence</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select review frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="biannual">Bi-Annually</SelectItem>
                        <SelectItem value="annual">Annually</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!editingCustomer && (
                <FormField
                  control={createCustomerForm.control}
                  name="firstReviewDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First review date</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <p className="text-sm text-muted-foreground">
                        We'll schedule the first review on this date. After that,
                        reviews repeat automatically based on the cadence above.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={createCustomerForm.control}
                  name="addressStreet"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Street Address</FormLabel>
                      <FormControl>
                        <Input placeholder="123 Main Street" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createCustomerForm.control}
                  name="addressCity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input placeholder="New York" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createCustomerForm.control}
                  name="addressState"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State/Province</FormLabel>
                      <FormControl>
                        <Input placeholder="NY" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createCustomerForm.control}
                  name="addressCountry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <FormControl>
                        <Input placeholder="USA" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createCustomerForm.control}
                  name="addressZipCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ZIP/Postal Code</FormLabel>
                      <FormControl>
                        <Input placeholder="10001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createCustomerMutation.isPending ||
                    updateCustomerMutation.isPending
                  }
                  onClick={() => {}}
                >
                  {editingCustomer
                    ? updateCustomerMutation.isPending
                      ? "Updating..."
                      : "Update Customer"
                    : createCustomerMutation.isPending
                      ? "Creating..."
                      : "Create Customer"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            <div className="flex-1 relative">
              <Input
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
                <SelectItem value="mid-market">Mid-market</SelectItem>
                <SelectItem value="small-business">Small Business</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort By */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt">Date Created</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="type">Type</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort Order */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="w-20"
            >
              <ArrowUpDown className="h-4 w-4 mr-1" />
              {sortBy === "createdAt"
                ? sortOrder === "asc"
                  ? "Old"
                  : "New"
                : sortOrder === "asc"
                  ? "A-Z"
                  : "Z-A"}
            </Button>

            {/* View Toggle */}
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className="rounded-r-none"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className="rounded-l-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customers Display */}
      <Card>
        <CardHeader>
          <CardTitle>All Customers</CardTitle>
        </CardHeader>

        <CardContent className="p-0">
          {viewMode === "grid" ? (
            // Grid View
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map((customer: any) => (
                  <Card
                    key={customer.id}
                    className={`hover:shadow-md transition-shadow ${customer.deletedFromPsa ? "opacity-60 border-destructive/50" : ""}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          {customer.logoUrl ? (
                            <img 
                              src={customer.logoUrl} 
                              alt={`${customer.name} logo`}
                              className="w-12 h-12 rounded-lg object-contain bg-white flex-shrink-0"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                if (e.currentTarget.nextElementSibling) {
                                  (e.currentTarget.nextElementSibling as HTMLElement).classList.remove('hidden');
                                }
                              }}
                            />
                          ) : null}
                          <div className={`w-12 h-12 bg-blue-50 dark:bg-blue-950 rounded-lg flex items-center justify-center flex-shrink-0 ${customer.logoUrl ? 'hidden' : ''}`}>
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                              {getInitials(customer.name)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <Link href={`/customers/${customer.id}`}>
                              <h3 className={`font-semibold text-foreground hover:text-primary cursor-pointer transition-colors truncate ${customer.deletedFromPsa ? "line-through text-muted-foreground" : ""}`}>
                                {customer.name}
                              </h3>
                            </Link>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {getTypeBadge(customer.type)}
                              {customer.deletedFromPsa && (
                                <Badge
                                  variant="destructive"
                                  className="text-xs"
                                >
                                  Removed from PSA
                                </Badge>
                              )}
                              {isPsaSynced(customer.id) && !customer.deletedFromPsa && (
                                <Badge
                                  variant="secondary"
                                  className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                                >
                                  <Building className="mr-1 h-2 w-2" />
                                  {getProviderDisplayName(getPsaProvider(customer.id))}{" "}
                                  Sync
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm mb-4">
                        {(customer.addressStreet ||
                          customer.addressCity ||
                          customer.addressState ||
                          customer.addressZipCode) && (
                          <div className="flex items-center space-x-2 text-muted-foreground">
                            <Building className="h-4 w-4" />
                            <span className="truncate">
                              {[
                                customer.addressStreet,
                                customer.addressCity,
                                customer.addressState,
                                customer.addressZipCode,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {shouldShowComponent(user, "editCustomer") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEdit(customer)}
                            className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                            title="Edit customer"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {shouldShowComponent(user, "deleteCustomer") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(customer)}
                            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete customer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex justify-between items-center text-xs text-muted-foreground">
                          <span>
                            Added{" "}
                            {new Date(customer.createdAt).toLocaleDateString()}
                          </span>
                          <span>ID: {customer.id}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full text-center py-6">
                  <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    No customers found
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {searchTerm || typeFilter !== "all"
                      ? "Try adjusting your search or filter criteria."
                      : "Start by adding your first customer."}
                  </p>
                </div>
              )}
            </div>
          ) : (
            // List View
            <div>
              {/* Mobile Cards - Show on small screens */}
              <div className="block lg:hidden p-4">
                {filteredCustomers.length > 0 ? (
                  <div className="space-y-3">
                    {filteredCustomers.map((customer: any) => (
                      <div
                        key={customer.id}
                        className={`p-4 bg-muted/30 rounded-lg border ${customer.deletedFromPsa ? "opacity-60 border-destructive/50" : ""}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="min-w-0 flex-1">
                            <Link href={`/customers/${customer.id}`}>
                              <h3 className={`font-medium text-blue-600 hover:text-blue-800 cursor-pointer hover:underline truncate ${customer.deletedFromPsa ? "line-through" : ""}`}>
                                {customer.name}
                              </h3>
                            </Link>
                            <p className="text-sm text-muted-foreground">
                              ID: {customer.id}
                            </p>
                          </div>
                          <div className="ml-3 flex-shrink-0 space-y-1">
                            {getTypeBadge(customer.type)}
                            {customer.deletedFromPsa && (
                              <Badge
                                variant="destructive"
                                className="text-xs block"
                              >
                                Removed from PSA
                              </Badge>
                            )}
                            {isPsaSynced(customer.id) && !customer.deletedFromPsa && (
                              <Badge
                                variant="secondary"
                                className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 block"
                              >
                                <Building className="mr-1 h-2 w-2" />
                                {getProviderDisplayName(getPsaProvider(customer.id))}{" "}
                                Sync
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Address:
                            </span>
                            <span className="text-foreground text-right truncate ml-2">
                              {[
                                customer.addressStreet,
                                customer.addressCity,
                                customer.addressState,
                                customer.addressZipCode,
                              ]
                                .filter(Boolean)
                                .join(", ") || "N/A"}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Added:
                            </span>
                            <span className="text-foreground">
                              {new Date(
                                customer.createdAt,
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {shouldShowComponent(user, "editCustomer") && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startEdit(customer)}
                              className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                              title="Edit Customer"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {shouldShowComponent(user, "deleteCustomer") && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(customer)}
                              title="Delete Customer"
                              className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      No customers found
                    </h3>
                    <p className="text-muted-foreground">
                      {searchTerm || typeFilter !== "all"
                        ? "Try adjusting your search or filter criteria."
                        : "Start by adding your first customer."}
                    </p>
                  </div>
                )}
              </div>

              {/* Desktop Table - Show on large screens */}
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-background">
                      <tr>
                        <th
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (sortBy === "name") {
                              setSortOrder(
                                sortOrder === "asc" ? "desc" : "asc",
                              );
                            } else {
                              setSortBy("name");
                              setSortOrder("asc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Customer</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "name" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (sortBy === "type") {
                              setSortOrder(
                                sortOrder === "asc" ? "desc" : "asc",
                              );
                            } else {
                              setSortBy("type");
                              setSortOrder("asc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Type</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "type" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Address
                        </th>
                        <th
                          className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (sortBy === "createdAt") {
                              setSortOrder(
                                sortOrder === "asc" ? "desc" : "asc",
                              );
                            } else {
                              setSortBy("createdAt");
                              setSortOrder("desc");
                            }
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Created</span>
                            <ArrowUpDown className="h-3 w-3" />
                            {sortBy === "createdAt" && (
                              <span className="text-primary">
                                {sortOrder === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-gray-200">
                      {filteredCustomers.length > 0 ? (
                        filteredCustomers.map((customer: any) => (
                          <tr key={customer.id} className={customer.deletedFromPsa ? "opacity-60" : ""}>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {customer.logoUrl ? (
                                  <img 
                                    src={customer.logoUrl} 
                                    alt={`${customer.name} logo`}
                                    className="w-10 h-10 rounded-lg object-contain bg-white flex-shrink-0"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      if (e.currentTarget.nextElementSibling) {
                                        (e.currentTarget.nextElementSibling as HTMLElement).classList.remove('hidden');
                                      }
                                    }}
                                  />
                                ) : null}
                                <div className={`w-10 h-10 bg-blue-50 dark:bg-blue-950 rounded-lg flex items-center justify-center flex-shrink-0 ${customer.logoUrl ? 'hidden' : ''}`}>
                                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                    {getInitials(customer.name)}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <Link href={`/customers/${customer.id}`}>
                                    <div className={`text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer hover:underline ${customer.deletedFromPsa ? "line-through" : ""}`}>
                                      {customer.name}
                                    </div>
                                  </Link>
                                  <div className="text-sm text-muted-foreground">
                                    ID: {customer.id}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex flex-wrap gap-1">
                                {getTypeBadge(customer.type)}
                                {customer.deletedFromPsa && (
                                  <Badge
                                    variant="destructive"
                                    className="text-xs"
                                  >
                                    Removed from PSA
                                  </Badge>
                                )}
                                {isPsaSynced(customer.id) && !customer.deletedFromPsa && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                                  >
                                    <Building className="mr-1 h-2 w-2" />
                                    {getProviderDisplayName(getPsaProvider(
                                      customer.id,
                                    ))}{" "}
                                    Sync
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground max-w-xs truncate">
                              {[
                                customer.addressStreet,
                                customer.addressCity,
                                customer.addressState,
                                customer.addressZipCode,
                              ]
                                .filter(Boolean)
                                .join(", ") || "N/A"}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                              {new Date(
                                customer.createdAt,
                              ).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground">
                              <div className="flex items-center space-x-2">
                                {shouldShowComponent(user, "editCustomer") && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => startEdit(customer)}
                                    title="Edit Customer"
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                {shouldShowComponent(
                                  user,
                                  "deleteCustomer",
                                ) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(customer)}
                                    title="Delete Customer"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-4 text-center text-sm text-muted-foreground"
                          >
                            No customers found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Import Customers</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Upload a CSV file to bulk import multiple customers at once.{" "}
              <Button
                variant="link"
                className="p-0 h-auto text-primary"
                onClick={downloadTemplate}
              >
                Download template
              </Button>{" "}
              to see the required format.
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">CSV File</label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                className="cursor-pointer"
              />
            </div>

            {importResults && (
              <div className="rounded-md bg-muted p-3 text-sm">
                <div className="font-medium text-foreground">
                  Import Results:
                </div>
                <div className="text-green-600">
                  {importResults.successful} customers imported successfully
                </div>
                {importResults.failed > 0 && (
                  <div className="text-red-600">
                    {importResults.failed} customers failed to import
                  </div>
                )}
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mt-2">
                    <div className="font-medium text-foreground">Errors:</div>
                    <ul className="list-disc list-inside text-red-600">
                      {importResults.errors
                        .slice(0, 5)
                        .map((error: string, index: number) => (
                          <li key={index}>{error}</li>
                        ))}
                      {importResults.errors.length > 5 && (
                        <li>
                          ...and {importResults.errors.length - 5} more errors
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportDialog(false);
                  setCsvFile(null);
                  setImportResults(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={!csvFile || importCustomersMutation.isPending}
              >
                {importCustomersMutation.isPending ? "Importing..." : "Import"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
